import React, { useState, useCallback } from 'react';
import { checkIsIntegerNumber, checkIsFloatNumber } from 'common/utils';

export type TextFieldValue = string;
export type handlerChangeTextFieldValue = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => void;
export type HandlerChangeTextFieldValue = handlerChangeTextFieldValue;
export type setTextFieldValueValue = React.Dispatch<React.SetStateAction<string>>;
export type resetTextFieldValue = () => void;
type changeCallback = (event: React.ChangeEvent<HTMLInputElement>) => void;
type FieldType = 'text' | 'int' | 'float';

type ValidateValue = (value: string) => boolean;
export interface Settings {
  fieldType?: FieldType;
  maxValue?: number;
  decimalNumberCount?: number;
  validateValue?: ValidateValue;
}

const DEFAULT_MAX_INT_VALUE = 999999999999999999999;

const checkIsFieldTypeText = (fieldType: FieldType) => (fieldType === 'text');
const checkIsFieldTypeNumber = (fieldType: FieldType) => (fieldType === 'int');
const checkIsFieldTypeFloat = (fieldType: FieldType) => (fieldType === 'float');

export const useTextFieldState = (
  initValue = '',
  changeCallback?: changeCallback,
  settings?: Settings
) => {
  const {
    fieldType = 'text',
    maxValue = DEFAULT_MAX_INT_VALUE,
    validateValue
  } = settings || {};
  const [value, setValue] = useState<TextFieldValue>(() => initValue);

  const handlerChangeValue = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { target: { value } } = event;
    if (
      checkIsFieldTypeText(fieldType)
      || (checkIsFieldTypeNumber(fieldType) && (value === '' || checkIsIntegerNumber(value)) && (Number(value) < maxValue))
      || (checkIsFieldTypeFloat(fieldType) && (value === '' || (validateValue ? validateValue(value) : checkIsFloatNumber(value))))
    ) setValue(value);
    if (changeCallback) changeCallback(event);
  }, [
    setValue,
    changeCallback,
    fieldType,
    maxValue,
    validateValue
  ]);

  const resetValue = useCallback(() => {
    setValue(initValue);
  }, [setValue, initValue]);

  return [value, handlerChangeValue, setValue, resetValue] as const;
};

export default useTextFieldState;
